import React, {ChangeEvent, useState} from 'react';
import {Flex} from "@chakra-ui/layout";
import {
  Alert,
  AlertIcon,
  AlertTitle,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Image,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Select,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import Plus from "../../../images/teacher/plus.svg";
import {NavLink} from "react-router-dom";
import {useSelector} from "react-redux";
import {StateType} from "../../../store/store";
import axios from "axios";

function TeacherLessons() {
  const {data: {token}, courses: {courses}} = useSelector((state: StateType) => state.rootReducer.teacherReducer);
  const toast = useToast();
  const {isOpen, onOpen, onClose} = useDisclosure();
  const teacherToken = token.includes("Bearer") ? token.slice(17, (token.length - 2)) : token.slice(10, (token.length - 2));
  const [video, setVideo] = useState<FileList | null>(null);
  const [videoDescription, setVideoDescription] = useState<string>("");
  const [videoName, setVideoName] = useState<string>("");
  const [courseId, setCourseId] = useState<string>("");
  const [videoError, setVideoError] = useState<string>("");
  const [fileError, setFileError] = useState<string>("");
  const [loaded, setLoaded] = useState(0);
  const [videoDescriptionError, setVideoDescriptionError] = useState<string>("");
  const [videoNameError, setVideoNameError] = useState<string>("");
  const [courseIdError, setCourseIdError] = useState<string>("");

  async function handleVideoUpload(e: ChangeEvent<HTMLInputElement>) {
    console.log(e.target.files);
    let videos = e.target.files;
    let size = 3221225472; //3gb
    // check size and length
    if (videos === null) return;
    if (videos.length > 3) {
      console.log("videos more than 3");
      setFileError("you cannot upload more than 3 Videos")
      videos = null;
      return;
    }
    // check size
    for (let i = 0; i < videos.length; i++) {
      if (videos[i].size > size) {
        setFileError(videos[i].type + "is too large, please pick a smaller file");
        return videos = null;
      }
    }
    setFileError("")
    const videoTypes = ['video/x-flv', 'video/mp4', 'application/x-mpegURL', 'video/MP2T', 'video/3gpp', 'video/x-ms-wmv', 'video/x-msvideo', 'video/avi'];
    for (let i = 0; i < videos.length; i++) {
      // compare video type, find doesn't match
      if (videoTypes.every(type => !(videos) || videos[i].type !== type)) {
        // create error message and assign to container
        setFileError(videos[i].type + ' is not a supported format');
        return videos = null;
      }
    }
    setFileError("")
    if (fileError !== "") {
      videos = null;
      return
    }
    setVideo(videos);
  }

  async function handleSubmit(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    e.preventDefault();
    if (videoName === "") {
      return setVideoNameError("Please type in a name for the video")
    }
    setVideoNameError("");
    if (videoName.length < 3 || videoName.length > 35) {
      return setVideoNameError("Ensure that the video name is not less than 3 or more than 35 characters")
    }
    setVideoNameError("");
    if (videoDescription === "") {
      return setVideoDescriptionError("Please type in a description for the video")
    }
    setVideoDescriptionError("");
    if (videoDescription.length < 10 || videoDescription.length > 100) {
      return setVideoDescriptionError("Ensure that the video description is not less than 10 or more than 100 characters")
    }
    setVideoDescriptionError("");
    if (courseId === "") {
      return setCourseIdError(" Please choose a course")
    }
    setCourseIdError("");
    if (video === null) {
      return setVideoError("Please choose a video to upload");
    }
    setVideoError("");
    const uploadData = new FormData();
    uploadData.append("videoName", videoName)
    uploadData.append("videoDescription", videoDescription)
    uploadData.append("courseId", courseId)
    for (let i = 0; i < video.length; i++) {
      uploadData.append(video[i].name, video[i])
    }
    const res = await axios.post("/user/teacher/upload-videos", uploadData, {
      headers: {
        'content-type': 'multipart/form-data',
        "Authorization": `Bearer ${teacherToken}`
      },
      onUploadProgress: progressEvent => {
        setLoaded(progressEvent.loaded / progressEvent.total * 100)
      }
    })
    console.log(res);
    toast({
      title: "Upload Status",
      description: `${res.data.message}`,
      status: "success",
      duration: 9000,
      isClosable: true,
    })
  }

  return (
    <Flex justifyContent={"center"} mt={10} flexDirection={"row"}>
      <Flex flexDirection={"column"}>
        <Button onClick={onOpen} bgColor={"white"} fontSize={"sm"} fontWeight={"semibold"} borderRadius={"2xl"}
                size={"lg"} mb={10}>
          <Image
            boxSize="30px"
            objectFit="cover"
            mr={"12px"}
            src={Plus}
            alt="add icon"
          />
          Lessons
        </Button>

        <Link as={NavLink} to={"/tutor/lessons/uploaded-lessons"} p={4} boxShadow="2xl" bgColor={"brand.darkGreen"}
              textAlign={"center"} borderRadius={"20px"} fontWeight={"bold"}
              _hover={{color: "blue", textDecoration: "none"}}>
          Uploaded Lessons
        </Link>
      </Flex>

      <Modal closeOnOverlayClick={true} isOpen={isOpen} onClose={onClose}>
        <ModalOverlay/>
        <ModalContent>
          <ModalHeader>Upload Lesson</ModalHeader>
          <ModalCloseButton/>
          <ModalBody pb={6}>
            <form encType="multipart/form-data">
              <Alert d={fileError === "" ? "none" : "flex"} status="error">
                <AlertIcon/>
                <AlertTitle mr={2}>{fileError}</AlertTitle>
              </Alert>
              <FormControl isInvalid={videoNameError !== ""}>
                <FormLabel htmlFor={"videoName"} fontWeight={"semibold"}>Video Name</FormLabel>
                <Input type={"text"} required minLength={3} maxLength={35} value={videoName}
                       onChange={(e) => setVideoName(e.target.value)}/>
                <FormErrorMessage>{videoNameError}</FormErrorMessage>
              </FormControl>
              <FormControl mt={3} isInvalid={videoDescriptionError !== ""}>
                <FormLabel htmlFor={"videoDescription"} fontWeight={"semibold"}>Video Description</FormLabel>
                <Input type={"text"} min={10} max={100} required value={videoDescription}
                       onChange={(e) => (setVideoDescription(e.target.value))}/>
                <FormErrorMessage>{videoDescriptionError}</FormErrorMessage>
              </FormControl>
              <FormControl mt={3} isInvalid={courseIdError !== ""}>
                <FormLabel htmlFor={"videoCourse"} fontWeight={"semibold"}>Course</FormLabel>
                <Select required value={courseId} onChange={(e) => setCourseId(e.target.value)}
                        placeholder="Choose course to upload lesson for">
                  {courses.map((course: any, index) => (
                    <option key={index} value={course.course_id}>{course.course_name}</option>
                  ))}
                </Select>
                <FormErrorMessage>{courseIdError}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={videoError !== null} my={5}>
                <FormLabel htmlFor={"video"} fontWeight={"semibold"}>Video</FormLabel>
                <input id={"video"} type="file" multiple onChange={(e) => handleVideoUpload(e)}/>
                <FormErrorMessage>{videoError}</FormErrorMessage>
              </FormControl>
              <Progress hasStripe value={loaded} max={100} mb={4}/>
              <Button type={"submit"} bgColor={"brand.darkGreen"} onClick={(e) => handleSubmit(e)}>upload
                video</Button>
            </form>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}

export default TeacherLessons;